import { loadStyle } from '@trp-dms-digital-shared/ds-ui-library-common';

import { Constants, Defaults } from '../common/constants.js';

/**
 * Loads Beacon's CDN-based files into the browser
 * @see https://ui-kit.gitlab-pages.awstrp.net/foundation/main/storybook/?path=/docs/usage-cdn--docs
 */
export class BeaconOrchestration {
  /**
   * When using `URL` the base arg will be trimmed
   *  until the first slash, so this ensures an
   *  ending slash
   */
  checkSlash(str: string): string {
    return str.endsWith('/') ? str : `${str}/`;
  }

  /**
   * Checks if orchestration skip Beacon setup
   */
  skipBeacon(): boolean {
    const skip = window[Constants.POP_CONFIGURATION].beaconSkip;
    if (skip !== false) {
      return true;
    }
    return false;
  }

  /**
   * Gets the projectName arg for beacon
   */
  getProjectName(): string {
    return window[Constants.POP_CONFIGURATION].beaconProjectName || '';
  }

  /**
   * Generate a assets URL root
   * @returns URL to a directory that contains Beacon assets
   */
  getBeaconRootUrl(): string {
    const assetsDomain =
      window[Constants.POP_CONFIGURATION].assetsDomain ||
      Defaults.ASSETS_DOMAIN;
    const beaconRoot = new URL(
      Constants.BEACON_URL_ROOT_PATH,
      this.checkSlash(assetsDomain),
    ).href;
    const beaconLibVersion =
      window[Constants.POP_CONFIGURATION].beaconVersion ||
      Defaults.BEACON_VERSION;
    return new URL(beaconLibVersion, this.checkSlash(beaconRoot)).href;
  }

  /**
   * Generate a beacon file URL
   * @param filepath - file path starting after version
   * @param beaconRootUrl - full Beacon url up to/including version
   * @returns full file url
   */
  getBeaconFileUrl(filepath: string, beaconRootUrl: string): string {
    return new URL(filepath, this.checkSlash(beaconRootUrl)).href;
  }

  /**
   * Adds <link> tags for beacon css to page header
   */
  loadBeaconStyles(beaconRootUrl: string): void {
    Object.values(Constants.BEACON_CSS_FILES).forEach(
      (filepath: string): void => {
        const styleUrl = this.getBeaconFileUrl(filepath, beaconRootUrl);
        loadStyle(styleUrl);
      },
    );
  }

  /**
   * Imports and runs the beacon components auto-registration script
   */
  async registerComponents(url: string): Promise<void> {
    await import(/* @vite-ignore */ url)
      .then(async (module) => {
        await module.autoLoadBeaconComponents(this.getProjectName());
      })
      .catch((error) =>
        console.error(Constants.LOG_IMPORT_BEACON_ERROR, error),
      );
  }

  /**
   * Loads beacon into a page
   */
  public async loadBeacon(): Promise<void> {
    if (this.skipBeacon()) {
      Promise.resolve();
      return;
    }
    console.warn(Constants.LOG_BEACON_ORCHESTRATE_WARN);
    const beaconRootUrl = this.getBeaconRootUrl();
    this.loadBeaconStyles(beaconRootUrl);
    const scriptUrl = this.getBeaconFileUrl(
      Constants.BEACON_REGISTER_PATH,
      beaconRootUrl,
    );
    await this.registerComponents(scriptUrl);
  }
}
